import {useEffect, useMemo, useState, type FormEvent} from "react";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import {useTranslation} from "react-i18next";
import {FloatingLabel} from "react-bootstrap";
import type {TournamentTypeValue} from "../../types/domain";
import { useOrganizableLadders } from "../../hooks/queries/useLadder";

interface TournamentFormData {
    name: string;
    type: TournamentTypeValue;
    setsNeededToWin: number;
    ladderId: number;
    organizer: string;
}

interface NewTournamentModalProps {
    isOpen: boolean;
    closeModal: () => void;
    handleSubmit: (tournament: TournamentFormData) => void | Promise<void>;
}

const isTournamentType = (value: string): value is TournamentTypeValue =>
    value === "SWISS" || value === "GROUPS" || value === "GROUPS_PLAYOFF" || value === "BLAST";

function NewTournamentModal(props: NewTournamentModalProps) {
    const { data: organizableLadders = [] } = useOrganizableLadders(props.isOpen);

    const emptyTournament = useMemo<TournamentFormData>(() => {
        return {
            name: "",
            type: "SWISS",
            setsNeededToWin: 2,
            ladderId: 0, // Sentinel until the organizable-ladders query resolves.
            organizer: "",
        }
    }, []);

    const { t, i18n } = useTranslation();
    const [ tournament, setTournament ] = useState<TournamentFormData>(emptyTournament)
    const [ submitButtonDisabled, setSubmitButtonDisabled ] = useState(false)
    const [validated, setValidated] = useState(false);

    useEffect(() => {
        if (props.isOpen === false) {
            setValidated(false)
            setTournament(emptyTournament)
            setSubmitButtonDisabled(false)
        }
    }, [emptyTournament, props.isOpen]);

    // Default the ladder to the first organizable one once the list loads.
    useEffect(() => {
        if (props.isOpen && organizableLadders.length > 0) {
            setTournament((prev) =>
                organizableLadders.some((l) => l.id === prev.ladderId)
                    ? prev
                    : { ...prev, ladderId: organizableLadders[0].id }
            );
        }
    }, [organizableLadders, props.isOpen]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        if (form.checkValidity() === false) {
            setValidated(true);
        } else {
            setSubmitButtonDisabled(true)
            // Re-enable the button if creation fails so the user can retry.
            Promise.resolve(props.handleSubmit(tournament)).catch(() => setSubmitButtonDisabled(false))
        }
    };

    return (
      <Modal
        show={props.isOpen}
        onHide={props.closeModal}
      >
          <Modal.Header closeButton>
              <Modal.Title>{t("addNewTournament")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Form.Group>
                      <FloatingLabel
                        controlId="floatingTournamentName"
                        label={t("tournamentName")}
                        className="mb-3"
                      >
                          <Form.Control required type="text" onChange={e => setTournament({...tournament, name: e.target.value})} value={tournament.name} placeholder=""/>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group>
                      <FloatingLabel
                        controlId="floatingSetsNeededToWin"
                        label={t("setsNeededToWin")}
                        className="mb-3"
                      >
                          <Form.Control required type="number" max="9" min="1" onChange={e => setTournament({...tournament, setsNeededToWin: Number(e.target.value)})} value={tournament.setsNeededToWin} placeholder=""/>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group className="mb-3">
                      <FloatingLabel
                        controlId="floatingTournamentSystem"
                        label={t("tournamentSystem")}
                        className="mb-3"
                      >
                          <Form.Select onChange={e => { const v = e.target.value; if (isTournamentType(v)) setTournament({...tournament, type: v}); }} value={tournament.type}>
                              <option value={"SWISS"}>{t("tournamentSystem_SWISS")}</option>
                              <option value={"GROUPS"}>{t("tournamentSystem_GROUPS")}</option>
                              <option value={"GROUPS_PLAYOFF"}>{t("tournamentSystem_GROUPS_PLAYOFF")}</option>
                          </Form.Select>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group className="mb-3">
                      <FloatingLabel
                        controlId="floatingLadder"
                        label={t("ladder")}
                        className="mb-3"
                      >
                          <Form.Select onChange={e => setTournament({...tournament, ladderId: Number(e.target.value)})} value={tournament.ladderId}>
                              {organizableLadders.map((l) => (
                                <option key={l.id} value={l.id}>{i18n.language === "cs" ? l.name : l.nameEn}</option>
                              ))}
                          </Form.Select>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group>
                      <FloatingLabel
                        controlId="floatingOrganizer"
                        label={t("organizer")}
                        className="mb-3"
                      >
                          <Form.Control required type="text" onChange={e => setTournament({...tournament, organizer: e.target.value})} value={tournament.organizer} placeholder=""/>
                      </FloatingLabel>
                  </Form.Group>
                  <div className="d-flex">
                      <Button type="submit" disabled={submitButtonDisabled || tournament.ladderId === 0} style={{flex: "1"}}>{t("createTournament")}</Button>
                  </div>
              </Form>
          </Modal.Body>
      </Modal>
    );
}

export default NewTournamentModal
